import isEmpty from 'lodash/isEmpty';

import {
  getSinkDatasetId,
  getSinkSystemId,
  getSourceDatasetIds,
  getSourceSystemId,
} from '../internals/pipes';

import type {
  PipeDeletedStatus,
  PipeConfigSource,
  PipeMap,
  PostPipesDeletionData,
} from '../types/pipes.types';
import type { DatasetID } from '../types/dataset.types';
import type { System } from '../types/system.types';

export const getSourceDatasetIdsFromArray = (datasets: DatasetID[]): string[] => {
  if (!Array.isArray(datasets)) {
    return [];
  }

  return datasets
    .filter((id) => typeof id === 'string' && id !== '')
    .map((id: string) => {
      const trimmed = id.trim();
      const result = trimmed.includes(' ')
        ? trimmed.substring(0, trimmed.lastIndexOf(' '))
        : trimmed;

      return result;
    });
};

export const getIdsFromConfigDatasets = (source: PipeConfigSource, replicaSource?: string) => {
  if (replicaSource) {
    return [replicaSource];
  }

  if (source?.dataset) {
    return [source.dataset];
  }

  if (source?.datasets && Array.isArray(source.datasets)) {
    return getSourceDatasetIdsFromArray(source.datasets);
  }

  return [];
};

export const isCurrentPipeUpdated = (currentPipeId: string, updatedPipes: string[]) => {
  if (!updatedPipes) {
    return false;
  }

  return updatedPipes.some((pipeId) => pipeId === currentPipeId);
};

export const getPipesCurlCommand = (subUrl: string, token: string, internal: boolean = false) => {
  if (!subUrl || !token) {
    return 'Error: Base url or token is missing!';
  }

  return `curl \
    -H "Authorization: bearer ${token}" \
    "${subUrl}/pipes${internal ? '?include-internal-pipes=true' : ''}"`;
};

export const getFromToObjectNameFn =
  (_direction: 'from' | 'to') => (pipeSource: PipeConfigSource, system?: System) => {
    // The e value is the human-assigned "name" attribute in the source and sink
    // config. If this is set, we will use it verbatim.
    if (isEmpty(pipeSource)) {
      return 'N/A';
    }

    if (pipeSource.name) {
      return pipeSource.name;
    }

    if (system) {
      const systemName = system.name || `${system._id} (${system?.config?.type})`;

      const systemSubComponent =
        pipeSource.dataset ||
        pipeSource['system-subcomponent'] ||
        pipeSource.table ||
        pipeSource.url;

      if (systemSubComponent) {
        return `${systemSubComponent}@${systemName}`;
      }
    }

    // check if the sink/source is connected to a dataset
    const datasetName = pipeSource.dataset;

    if (datasetName) {
      return `dataset:${datasetName}`;
    }

    // Finally, fallback to raw sink and source ids if nothing else has been found.
    return pipeSource._id;
  };

/*
  This util function responsible for iterating deleted pipes and array and build 2 arrays for store post actions cleanups.
*/
export const buildPipesDeletionPostActionsData = (pipes?: PipeMap, deletedPipes?: string[]) => {
  if (!pipes || !deletedPipes) {
    return { postDeleteBatchPipes: [], pipeActionStatus: [] };
  }

  const pipeActionStatus: PipeDeletedStatus[] = [];
  const postDeleteBatchPipes: PostPipesDeletionData[] = [];

  // OBS: for loop here over foreach for performance. Same for `array.push` method usage.
  for (let i = 0; i < deletedPipes.length; i++) {
    const pipeId = deletedPipes[i];
    const pipe = pipes[pipeId];

    const postDelete = {
      id: pipeId,
      sinkDatasetId: getSinkDatasetId(pipe),
      sourceDatasetIds: getSourceDatasetIds(pipe),
      sinkSystemId: getSinkSystemId(pipe),
      sourceSystemId: getSourceSystemId(pipe),
    };

    pipeActionStatus.push({ id: pipeId, state: 'deleting' });
    postDeleteBatchPipes.push(postDelete);
  }

  return { postDeleteBatchPipes, pipeActionStatus };
};
